import { resolveCapacity } from "../economy/entries.js";
export function queueTaskCommand(task, expectedRevision) {
    return {
        id: `task:${task.id}:queue`,
        ...(expectedRevision === undefined ? {} : { expectedRevision }),
        execute: (transaction) => {
            if (!transaction.isScopeActive(task.scope))
                transaction.reject({ code: "disabled", actionId: task.id, reasonKey: "scope-inactive" });
            const state = transaction.getTaskState(task.id);
            const outstanding = state.queue.length + (state.active ? 1 : 0);
            if (outstanding >= task.queueLimit)
                transaction.reject({ code: "budget-exceeded", budgetId: task.id });
            reserveInputs(task, transaction);
            const sequence = state.nextSequence + 1n;
            const queued = {
                sequence,
                escrow: entriesRecord(task.inputs),
                outputs: entriesRecord(task.outputs),
            };
            transaction.setTaskState(task.id, startNext(task, { ...state, nextSequence: sequence, queue: [...state.queue, queued] }));
        },
    };
}
export function cancelTaskCommand(task, sequence, expectedRevision) {
    return {
        id: `task:${task.id}:cancel`,
        ...(expectedRevision === undefined ? {} : { expectedRevision }),
        execute: (transaction) => {
            const state = transaction.getTaskState(task.id);
            const claim = requiredClaim(cancellationClaim(task, state, sequence, transaction.numbers), task, sequence, transaction);
            const active = state.active?.sequence === sequence ? null : state.active;
            const queue = state.queue.filter((entry) => entry.sequence !== sequence);
            transaction.setTaskState(task.id, startNext(task, { ...state, active, queue, refunds: [...state.refunds, claim] }));
        },
    };
}
export function claimTaskRefundCommand(task, sequence, expectedRevision) {
    return {
        id: `task:${task.id}:refund`,
        ...(expectedRevision === undefined ? {} : { expectedRevision }),
        execute: (transaction) => {
            const state = transaction.getTaskState(task.id);
            const claim = requiredClaim(state.refunds.find((entry) => entry.sequence === sequence), task, sequence, transaction);
            for (const [resource] of task.inputs) {
                const refund = claim.quantities[resource.id];
                if (refund !== undefined)
                    transaction.add(resource, refund);
            }
            transaction.setTaskState(task.id, {
                ...state,
                refunds: state.refunds.filter((entry) => entry.sequence !== sequence),
            });
        },
    };
}
export function runTasks(transaction, definitions, stepMs) {
    for (const task of [...definitions].sort((a, b) => a.id.localeCompare(b.id))) {
        if (!transaction.isScopeActive(task.scope))
            continue;
        let state = startNext(task, transaction.getTaskState(task.id));
        if (!state.active)
            continue;
        const active = progressActive(task, state.active, stepMs, transaction);
        state = { ...state, active };
        if (isFinished(active) && canDeliver(task, transaction)) {
            const delivered = deliver(task, transaction);
            state = startNext(task, {
                ...state,
                active: null,
                completed: [...state.completed, { sequence: active.sequence, quantities: delivered }],
            });
        }
        transaction.setTaskState(task.id, state);
    }
}
function startNext(task, state) {
    if (state.active || state.queue.length === 0)
        return state;
    const [next, ...queue] = state.queue;
    if (!next)
        return state;
    const active = task.work.kind === "fixed-duration"
        ? { ...next, mode: "fixed-duration", remainingMs: task.work.durationMs }
        : { ...next, mode: "current-rate", remainingWork: task.work.work };
    return { ...state, queue, active };
}
function reserveInputs(task, transaction) {
    for (const [resource, required] of task.inputs) {
        const available = transaction.get(resource);
        if (transaction.numbers.cmp(available, required) < 0)
            transaction.reject({ code: "insufficient", resourceId: resource.id, required, available });
    }
    const zero = transaction.numbers.fromNumber(0);
    for (const [resource, amount] of task.inputs)
        transaction.add(resource, transaction.numbers.sub(zero, amount));
}
function progressActive(task, active, stepMs, transaction) {
    if (active.mode === "fixed-duration")
        return { ...active, remainingMs: Math.max(0, active.remainingMs - stepMs) };
    const rate = task.work.kind === "current-rate" ? task.work.rate(transaction) : 0;
    if (!Number.isFinite(rate) || rate < 0)
        throw new TypeError(`Task ${task.id} returned an invalid rate`);
    return { ...active, remainingWork: Math.max(0, active.remainingWork - rate * (stepMs / 1000)) };
}
function isFinished(active) {
    return active.mode === "fixed-duration" ? active.remainingMs === 0 : active.remainingWork === 0;
}
function canDeliver(task, transaction) {
    if (task.delivery === "discard-overflow")
        return true;
    return task.outputs.every(([resource, amount]) => {
        const capacity = resolveCapacity(resource, (entry) => transaction.get(entry), transaction.numbers);
        if (capacity === undefined)
            return true;
        return (transaction.numbers.cmp(transaction.numbers.add(transaction.get(resource), amount), capacity) <= 0);
    });
}
function deliver(task, transaction) {
    const delivered = {};
    for (const [resource, amount] of task.outputs) {
        const before = transaction.get(resource);
        const desired = transaction.numbers.add(before, amount);
        const capacity = resolveCapacity(resource, (entry) => transaction.get(entry), transaction.numbers);
        const after = task.delivery === "discard-overflow" &&
            capacity !== undefined &&
            transaction.numbers.cmp(desired, capacity) > 0
            ? capacity
            : desired;
        transaction.set(resource, after);
        delivered[resource.id] = transaction.numbers.sub(transaction.get(resource), before);
    }
    return delivered;
}
function cancellationClaim(task, state, sequence, numbers) {
    const item = state.active?.sequence === sequence
        ? state.active
        : state.queue.find((entry) => entry.sequence === sequence);
    if (!item)
        return undefined;
    const ratio = task.cancellation.refund === "full"
        ? numbers.fromNumber(1)
        : task.cancellation.refund === "none"
            ? numbers.fromNumber(0)
            : task.cancellation.ratio;
    return {
        sequence,
        quantities: Object.fromEntries(Object.entries(item.escrow).map(([id, value]) => [id, numbers.mul(value, ratio)])),
    };
}
function entriesRecord(entries) {
    return Object.fromEntries(entries.map(([resource, amount]) => [resource.id, amount]));
}
function requiredClaim(claim, task, sequence, transaction) {
    if (!claim)
        transaction.reject({ code: "invalid-target", id: `${task.id}:${sequence}` });
    return claim;
}
//# sourceMappingURL=runtime.js.map